import React, { useEffect } from "react";
import { useForm, useFieldArray, Controller, Control } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import AppInputNew from "../../Core/AppInput/AppInputNew";
import AppSelect from "../../Core/AppSelect/AppSelect";
import AppText from "../../Core/AppText/AppText";
import Button from "../../Core/Button/Button";
import { useTranslation } from "react-i18next";
import Organisation from "@/models/Organisation";

// Define the weekday type
const WEEKDAYS = [
	"monday",
	"tuesday",
	"wednesday",
	"thursday",
	"friday",
	"saturday",
	"sunday",
] as const;

type Weekday = typeof WEEKDAYS[number];

// Zod schema for a single OB rule row (weekday mode)
const obRuleWeekdaySchema = z.object({
	id: z.string().optional(), // For existing rules
	mode: z.literal("weekday").optional().default("weekday"),
	name: z.string().optional().nullable(),
	weekdays: z.array(z.enum(WEEKDAYS)).min(1, "SELECT_AT_LEAST_ONE_WEEKDAY"),
	start: z
		.string()
		.regex(/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/, "INVALID_TIME_FORMAT")
		.min(1, "FIELD_IS_REQUIRED"),
	end: z
		.string()
		.regex(/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/, "INVALID_TIME_FORMAT")
		.min(1, "FIELD_IS_REQUIRED"),
	salarySupplement: z.number().min(0, "MUST_BE_POSITIVE_OR_ZERO").optional().nullable(),
	invoiceSupplement: z.number().min(0, "MUST_BE_POSITIVE_OR_ZERO").optional().nullable(),
	salaryType: z.string().optional().nullable(),
});

// Zod schema for a single OB rule row (date mode)
const obRuleDateSchema = z.object({
	id: z.string().optional(), // For existing rules
	mode: z.literal("date"),
	date: z.string().min(1, "FIELD_IS_REQUIRED"),
	name: z.string().min(1, "FIELD_IS_REQUIRED"),
	start: z
		.string()
		.regex(/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/, "INVALID_TIME_FORMAT")
		.min(1, "FIELD_IS_REQUIRED"),
	end: z
		.string()
		.regex(/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/, "INVALID_TIME_FORMAT")
		.min(1, "FIELD_IS_REQUIRED"),
	salarySupplement: z.number().min(0, "MUST_BE_POSITIVE_OR_ZERO").optional().nullable(),
	invoiceSupplement: z.number().min(0, "MUST_BE_POSITIVE_OR_ZERO").optional().nullable(),
	salaryType: z.string().optional().nullable(),
});

// Union type for OB rule
const obRuleSchema = z.discriminatedUnion("mode", [obRuleWeekdaySchema, obRuleDateSchema]);

// Form schema with array of rules (allow empty array to show empty state)
const obMatrixSchema = z.object({
	rules: z.array(obRuleSchema),
});

export type OBRule = z.infer<typeof obRuleSchema>;
export type OBMatrixFormData = z.infer<typeof obMatrixSchema>;

export interface SalaryCode {
	key: string;
	code: string;
	label: string;
}

// Helper function to extract OB salary codes from organisation
export function extractOBSalaryCodes(organisation: Organisation): SalaryCode[] {
	if (!organisation?.salarySettings?.salaryCodes) {
		return [];
	}

	const { salaryCodes } = organisation.salarySettings;
	const obCodes: SalaryCode[] = [];

	// Extract ob1-ob5
	for (let i = 1; i <= 5; i++) {
		const key = `ob${i}`;
		// @ts-ignore
		const code = salaryCodes[key];
		if (code && code !== "0") {
			obCodes.push({
				key,
				code,
				label: `OB${i} (${code})`,
			});
		}
	}

	return obCodes;
}

export interface OBMatrixProps {
	value: OBRule[];
	onChange: (rules: OBRule[]) => void;
	disabled?: boolean;
	mode?: "weekday" | "date";
	onValidationChange?: (isValid: boolean) => void;
	onValidationTrigger?: (triggerFn: { fn: () => Promise<boolean> }) => void;
	salaryCodes?: SalaryCode[];
}

const OBMatrix: React.FC<OBMatrixProps> = ({
	value,
	onChange,
	disabled = false,
	mode = "weekday",
	onValidationChange,
	onValidationTrigger,
	salaryCodes = [],
}) => {
	const { t } = useTranslation();

	const {
		control,
		watch,
		formState: { errors, isValid },
		trigger,
	} = useForm<OBMatrixFormData>({
		resolver: zodResolver(obMatrixSchema),
		defaultValues: {
			rules: value,
		},
		mode: "onBlur", // Only validate on blur, not on every change
	});

	const { fields, append, remove } = useFieldArray({
		control,
		name: "rules",
	});

	const rules = watch("rules");

	// Notify parent of changes
	useEffect(() => {
		onChange(rules);
	}, [rules, onChange]);

	// Notify parent of validation state changes
	useEffect(() => {
		if (onValidationChange) {
			onValidationChange(isValid);
		}
	}, [isValid, onValidationChange]);

	// Expose trigger function to parent
	useEffect(() => {
		if (onValidationTrigger) {
			// Wrap trigger function in an object to avoid React state function initialization issue
			onValidationTrigger({ fn: trigger });
		}
	}, [trigger, onValidationTrigger]);

	const getWeekdayLabel = (day: Weekday): string => {
		const labels: Record<Weekday, string> = {
			monday: "Mån",
			tuesday: "Tis",
			wednesday: "Ons",
			thursday: "Tor",
			friday: "Fre",
			saturday: "Lör",
			sunday: "Sön",
		};
		return labels[day];
	};

	// Render header based on mode
	const renderHeader = () => {
		if (mode === "date") {
			return (
				<div className="grid grid-cols-[2fr_2fr_1fr_1fr_1fr_1fr_1fr_auto] gap-2 mb-2 px-2">
					<AppText size="xs" weight="bold" color="grayText">
						Namn
					</AppText>
					<AppText size="xs" weight="bold" color="grayText">
						Datum
					</AppText>
					<AppText size="xs" weight="bold" color="grayText">
						Starttid
					</AppText>
					<AppText size="xs" weight="bold" color="grayText">
						Sluttid
					</AppText>
					<AppText size="xs" weight="bold" color="grayText">
						Lönetillägg (SEK)
					</AppText>
					<AppText size="xs" weight="bold" color="grayText">
						Fakturatillägg (SEK)
					</AppText>
					<AppText size="xs" weight="bold" color="grayText">
						Löneart
					</AppText>
					<div className="w-10"></div>
				</div>
			);
		}

		return (
			<div className="grid grid-cols-[1.5fr_2fr_1fr_1fr_1fr_1fr_1fr_auto] gap-2 mb-2 px-2">
				<AppText size="xs" weight="bold" color="grayText">
					Namn
				</AppText>
				<AppText size="xs" weight="bold" color="grayText">
					Veckodagar
				</AppText>
				<AppText size="xs" weight="bold" color="grayText">
					Starttid
				</AppText>
				<AppText size="xs" weight="bold" color="grayText">
					Sluttid
				</AppText>
				<AppText size="xs" weight="bold" color="grayText">
					Lönetillägg (SEK)
				</AppText>
				<AppText size="xs" weight="bold" color="grayText">
					Fakturatillägg (SEK)
				</AppText>
				<AppText size="xs" weight="bold" color="grayText">
					Löneart
				</AppText>
				<div className="w-10"></div>
			</div>
		);
	};

	// Render first columns based on individual rule mode
	const renderFirstColumns = (index: number) => {
		const rule = rules[index];

		if (rule.mode === "date") {
			return (
				<>
					{/* Name */}
					<Controller
						control={control}
						name={`rules.${index}.name` as any}
						render={({ field: fieldProps }) => (
							<AppInputNew
								{...fieldProps}
								type="text"
								placeholder="T.ex. Julafton"
								disabled={disabled}
								errors={(errors.rules?.[index] as any)?.name?.message}
							/>
						)}
					/>

					{/* Date */}
					<Controller
						control={control}
						name={`rules.${index}.date` as any}
						render={({ field: fieldProps }) => (
							<AppInputNew
								{...fieldProps}
								type="date"
								disabled={disabled}
								errors={(errors.rules?.[index] as any)?.date?.message}
							/>
						)}
					/>
				</>
			);
		}

		// Weekday mode - Name + Weekdays
		return (
			<>
				{/* Name */}
				<Controller
					control={control}
					name={`rules.${index}.name` as any}
					render={({ field: fieldProps }) => (
						<AppInputNew
							{...fieldProps}
							type="text"
							placeholder="T.ex. Kvällstillägg"
							disabled={disabled}
							value={fieldProps.value ?? ""}
							errors={(errors.rules?.[index] as any)?.name?.message}
						/>
					)}
				/>

				{/* Weekdays */}
				<Controller
					control={control}
					name={`rules.${index}.weekdays` as any}
					render={({ field: fieldProps }) => (
						<div className="relative">
							<div className="flex flex-wrap gap-1 p-1 border border-gray-300 rounded-sm bg-white min-w-[270px] min-h-[2.3125rem]">
								{WEEKDAYS.map((day) => (
									<button
										key={day}
										type="button"
										disabled={disabled}
										onClick={() => {
											const currentDays = fieldProps.value || [];
											const newDays = currentDays.includes(day)
												? currentDays.filter((d: Weekday) => d !== day)
												: [...currentDays, day];
											fieldProps.onChange(newDays);
										}}
										className={`
											px-2 py-1 text-xs rounded transition-colors
											${
												fieldProps.value?.includes(day)
													? "bg-green-600 text-white"
													: "bg-gray-200 text-gray-700 hover:bg-gray-300"
											}
											${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}
										`}
									>
										{getWeekdayLabel(day)}
									</button>
								))}
							</div>
							{errors.rules && (errors.rules[index] as any)?.weekdays && (
								<AppText margin="0.35rem 0 0 0.15rem" size="xs" color="danger">
									{t((errors.rules[index] as any)?.weekdays?.message as any)}
								</AppText>
							)}
						</div>
					)}
				/>
			</>
		);
	};

	// Sort rules: weekday rules first, then date rules
	const sortedIndices = fields
		.map((field, index) => ({ field, index, rule: rules[index] }))
		.sort((a, b) => {
			const aIsDate = a.rule.mode === "date" ? 1 : 0;
			const bIsDate = b.rule.mode === "date" ? 1 : 0;
			return aIsDate - bIsDate;
		});

	// Check if we have both types for divider
	const hasWeekdayRules = rules.some((r) => r.mode !== "date");
	const hasDateRules = rules.some((r) => r.mode === "date");
	const showDivider = hasWeekdayRules && hasDateRules;

	// Find the index where date rules start (for divider placement)
	const firstDateRuleIndex = sortedIndices.findIndex((item) => item.rule.mode === "date");

	return (
		<div className="w-full">
			{/* Header Row */}
			{renderHeader()}

			{/* Rows */}
			{sortedIndices.map(({ field, index }, sortedIndex) => {
				const gridCols =
					rules[index].mode === "date"
						? "grid-cols-[2fr_2fr_1fr_1fr_1fr_1fr_1fr_auto]"
						: "grid-cols-[1.5fr_2fr_1fr_1fr_1fr_1fr_1fr_auto]";

				return (
					<React.Fragment key={field.id}>
						{/* Divider between weekday and date rules */}
						{showDivider && sortedIndex === firstDateRuleIndex && (
							<div className="my-6 px-2">
								<div className="flex items-center gap-3">
									<div className="flex-1 h-px bg-gray-300"></div>
									<AppText size="xs" weight="bold" color="grayText">
										Datumregler
									</AppText>
									<div className="flex-1 h-px bg-gray-300"></div>
								</div>
							</div>
						)}

						<div className="mb-4">
							<div className={`grid ${gridCols} gap-2 items-start`}>
								{/* First Column(s) - Weekdays or Name+Date */}
								{renderFirstColumns(index)}

								{/* Start Time */}
								<Controller
									control={control}
									name={`rules.${index}.start`}
									render={({ field: fieldProps }) => (
										<AppInputNew
											{...fieldProps}
											type="time"
											placeholder="00:00"
											disabled={disabled}
											errors={errors.rules?.[index]?.start?.message}
										/>
									)}
								/>

								{/* End Time */}
								<Controller
									control={control}
									name={`rules.${index}.end`}
									render={({ field: fieldProps }) => (
										<AppInputNew
											{...fieldProps}
											type="time"
											placeholder="23:59"
											disabled={disabled}
											errors={errors.rules?.[index]?.end?.message}
										/>
									)}
								/>

								{/* Salary Supplement */}
								<Controller
									control={control}
									name={`rules.${index}.salarySupplement`}
									render={({ field: fieldProps }) => (
										<AppInputNew
											{...fieldProps}
											type="number"
											min={0}
											placeholder="0"
											disabled={disabled}
											value={fieldProps.value ?? ""}
											onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
												const val = e.target.value === "" ? null : Number(e.target.value);
												fieldProps.onChange(val);
											}}
											errors={errors.rules?.[index]?.salarySupplement?.message}
										/>
									)}
								/>

								{/* Invoice Supplement */}
								<Controller
									control={control}
									name={`rules.${index}.invoiceSupplement`}
									render={({ field: fieldProps }) => (
										<AppInputNew
											{...fieldProps}
											type="number"
											min={0}
											placeholder="0"
											disabled={disabled}
											value={fieldProps.value ?? ""}
											onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
												const val = e.target.value === "" ? null : Number(e.target.value);
												fieldProps.onChange(val);
											}}
											errors={errors.rules?.[index]?.invoiceSupplement?.message}
										/>
									)}
								/>

								{/* Salary Type */}
								<Controller
									control={control}
									name={`rules.${index}.salaryType`}
									render={({ field: fieldProps }) => (
										<AppSelect
											{...fieldProps}
											options={salaryCodes}
											getOptionLabel={(option) => option.label}
											getOptionValue={(option) => option.code}
											placeholder="Välj löneart"
											disabled={disabled}
											value={fieldProps.value ?? ""}
											errors={errors.rules?.[index]?.salaryType?.message}
											margin="0"
										/>
									)}
								/>

								{/* Remove Button */}
								<div className="flex items-center">
									<button
										type="button"
										onClick={() => remove(index)}
										disabled={disabled}
										className={`
										w-10 h-9 flex items-center justify-center rounded-sm
										transition-colors
										${
											disabled
												? "bg-gray-200 text-gray-400 cursor-not-allowed"
												: "bg-red-500 text-white hover:bg-red-600"
										}
									`}
										title="Ta bort rad"
									>
										×
									</button>
								</div>
							</div>
						</div>
					</React.Fragment>
				);
			})}

			{/* Add Row Button */}
			<div className="mt-4 flex flex-row justify-end gap-2">
				<Button
					type="button"
					onClick={() => append(createEmptyRule("weekday"))}
					disabled={disabled}
					backgroundColor="buttonGray"
					textColor="black"
					fontSize="s"
				>
					+ Regel för <u>veckodag</u>
				</Button>
				<Button
					type="button"
					onClick={() => append(createEmptyRule("date"))}
					disabled={disabled}
					backgroundColor="buttonGray"
					textColor="black"
					fontSize="s"
				>
					+ Regel för <u>datum</u>
				</Button>
			</div>
		</div>
	);
};

// Helper function to create an empty rule
function createEmptyRule(mode: "weekday" | "date" = "weekday"): OBRule {
	if (mode === "date") {
		return {
			mode: "date",
			date: "",
			name: "",
			start: "",
			end: "",
			salarySupplement: null,
			invoiceSupplement: null,
			salaryType: null,
		};
	}
	return {
		mode: "weekday",
		name: null,
		weekdays: [],
		start: "",
		end: "",
		salarySupplement: null,
		invoiceSupplement: null,
		salaryType: null,
	};
}

export default OBMatrix;

// Controller wrapper for React Hook Form integration
export interface OBMatrixControllerProps extends Omit<OBMatrixProps, "value" | "onChange"> {
	control: Control<any, any>;
	name: string;
	rules?: object;
}

export function OBMatrixController({ control, name, rules, ...rest }: OBMatrixControllerProps) {
	return (
		<Controller
			name={name}
			control={control}
			rules={rules}
			render={({ field: { value, onChange } }) => (
				<OBMatrix {...rest} value={value || []} onChange={onChange} />
			)}
		/>
	);
}
